import { useCallback, useEffect, useRef, useState } from 'react'
import { invokeEdgeFunction } from './supabaseEdge'

const POLL_MS = 2000
const POLL_MAX_MS = 90000

/**
 * Loads cached 24h platform report for super-admin dashboard band.
 */
export function usePlatformDailyReport() {
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState('')
  const [running, setRunning] = useState(null)
  const [latest, setLatest] = useState(null)
  const [lastFailed, setLastFailed] = useState(null)

  const pollRef = useRef(null)
  const pollStartedRef = useRef(0)
  const loadGenRef = useRef(0)

  const applyPayload = useCallback((body) => {
    setRunning(body.running ?? null)
    setLatest(body.latest ?? null)
    setLastFailed(body.last_failed ?? null)
  }, [])

  const load = useCallback(async () => {
    const gen = ++loadGenRef.current
    setError('')
    try {
      const res = await invokeEdgeFunction('platform-daily-report', { method: 'GET' })
      const body = await res.json().catch(() => ({}))
      if (gen !== loadGenRef.current) return null
      if (!res.ok) {
        throw new Error(body.error || body.message || `Report fetch failed (${res.status})`)
      }
      applyPayload(body)
      return body
    } catch (e) {
      if (gen === loadGenRef.current) {
        setError(e?.message || String(e))
      }
      return null
    } finally {
      if (gen === loadGenRef.current) {
        setLoading(false)
      }
    }
  }, [applyPayload])

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current)
      pollRef.current = null
    }
    pollStartedRef.current = 0
  }, [])

  const startPolling = useCallback(() => {
    stopPolling()
    pollStartedRef.current = Date.now()
    pollRef.current = window.setInterval(async () => {
      if (Date.now() - pollStartedRef.current > POLL_MAX_MS) {
        stopPolling()
        setGenerating(false)
        setError('Report generation timed out. Try again.')
        return
      }
      const body = await load()
      if (!body?.running) {
        stopPolling()
        setGenerating(false)
      }
    }, POLL_MS)
  }, [load, stopPolling])

  useEffect(() => {
    void load().then((body) => {
      if (body?.running) {
        setGenerating(true)
        startPolling()
      }
    })
    return () => {
      loadGenRef.current += 1
      stopPolling()
    }
  }, [load, startPolling, stopPolling])

  const generate = useCallback(async () => {
    if (generating) return
    setGenerating(true)
    setError('')
    try {
      const res = await invokeEdgeFunction('platform-daily-report', {
        method: 'POST',
        body: JSON.stringify({ trigger: 'manual' }),
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok && res.status !== 202) {
        throw new Error(body.error || body.message || `Generate failed (${res.status})`)
      }
      await load()
      if (body.status === 'running' || res.status === 202) {
        startPolling()
      } else {
        setGenerating(false)
      }
    } catch (e) {
      setGenerating(false)
      setError(e?.message || String(e))
    }
  }, [generating, load, startPolling])

  const displayReport = latest || (running?.status === 'success' ? running : null)
  const isRunning = Boolean(running?.status === 'running') || generating

  return {
    loading,
    generating: isRunning,
    error,
    running,
    latest,
    lastFailed,
    displayReport,
    refresh: load,
    generate,
  }
}
